// packages/ui-kit/src/primitives/Skeleton.tsx
// C3F v18 — reference: design/system/components/feedback/Skeleton.{jsx,prompt.md}
//
// Adherent skeleton primitives — dimension-true placeholders sized to mirror the
// app's real content shapes (widget card, list item, stat card, table rows, hero,
// form). Built on a single shimmer recipe so the look has one source of truth.
//
// Convention: a view renders its shell/header immediately and swaps ONLY the data
// region for the matching primitive below, sized to the typical loaded content — so
// the reserved height equals the loaded height and nothing jumps.
import type { ReactNode } from "react";

type DataAttrs = Record<`data-${string}`, string>;

// DS shimmer: px-shimmer sweeping the hover/active veils. Veils never host text —
// these blocks are pure decoration, which is exactly what veils are for. Because
// the gradient is token-driven it also holds inside `surface-night` (see Hero).
const shimmerCls =
  "bg-[linear-gradient(90deg,var(--bg-hover)_25%,var(--bg-active)_37%,var(--bg-hover)_63%)] [background-size:400%_100%] [animation:px-shimmer_1.4s_ease_infinite]";

/** The one shimmer rectangle — the single source of the skeleton look. */
export function SkeletonBlock({
  w,
  h = "1rem",
  rounded = "rounded-xs",
  className = "",
  ...rest
}: {
  w?: string;
  h?: string;
  rounded?: string;
  className?: string;
} & Partial<DataAttrs>) {
  return (
    <span
      {...rest}
      aria-hidden
      className={`block ${shimmerCls} ${rounded} ${className}`}
      style={{ width: w ?? "100%", height: h }}
    />
  );
}

export function SkeletonLine({ w = "100%" }: { w?: string }) {
  return <SkeletonBlock w={w} h="0.875rem" className="my-0.5" />;
}

export function SkeletonCircle({ size = 24 }: { size?: number }) {
  return <SkeletonBlock w={`${size}px`} h={`${size}px`} rounded="rounded-full" />;
}

/** Visually-hidden live label — one per region, not per block. */
function Region({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div role="status" aria-busy="true" className={className}>
      <span className="sr-only">Caricamento…</span>
      {children}
    </div>
  );
}

/** Eyebrow row: 14px square + short line — matches widget/section headers. */
export function SkeletonEyebrow() {
  return (
    <div className="mb-2 flex items-center gap-2" aria-hidden>
      <SkeletonBlock w="14px" h="14px" />
      <SkeletonBlock w="40%" h="0.75rem" />
    </div>
  );
}

/** Matches a home widget: <Card> p-4 + eyebrow + N text lines. Cards are 16px. */
export function SkeletonWidgetCard({
  lines = 3,
  accent = false,
}: {
  lines?: number;
  accent?: boolean;
}) {
  return (
    <Region
      className={`rounded-lg border p-4 ${
        accent ? "border-accent-strong bg-accent-soft" : "border-border-default bg-elevated"
      }`}
    >
      <SkeletonEyebrow />
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <SkeletonBlock key={i} data-skel-line="" w={i % 2 === 0 ? "85%" : "60%"} h="0.875rem" />
        ))}
      </div>
    </Region>
  );
}

/** Matches PeopleList/SopList row: avatar 24 + 2 lines + trailing badge (pill). */
export function SkeletonCardListItem() {
  return (
    <div
      aria-hidden
      className="flex items-center gap-3 rounded-lg border border-border-default bg-elevated p-3"
    >
      <SkeletonCircle size={24} />
      <div className="flex-1 space-y-1.5">
        <SkeletonBlock w="45%" h="0.875rem" />
        <SkeletonBlock w="30%" h="0.75rem" />
      </div>
      <SkeletonBlock w="56px" h="1.25rem" rounded="rounded-pill" />
    </div>
  );
}

export function SkeletonList({ count, item }: { count: number; item: ReactNode }) {
  return (
    <Region className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} data-skel-item="">
          {item}
        </div>
      ))}
    </Region>
  );
}

export function SkeletonStatCard() {
  return (
    <div
      data-skel-stat=""
      aria-hidden
      className="space-y-2 rounded-lg border border-border-default bg-elevated p-4"
    >
      <SkeletonBlock w="50%" h="0.75rem" />
      <SkeletonBlock w="40%" h="1.75rem" />
    </div>
  );
}

export function SkeletonStatGrid({ count = 4 }: { count?: number }) {
  return (
    <Region className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonStatCard key={i} />
      ))}
    </Region>
  );
}

export function SkeletonTableRows({
  rows = 5,
  cols = 6,
  firstSticky = true,
}: {
  rows?: number;
  cols?: number;
  firstSticky?: boolean;
}) {
  return (
    <Region className="overflow-hidden rounded-lg border border-border-default">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex border-b border-border-default last:border-0">
          {Array.from({ length: cols }).map((_, c) => (
            <div
              key={c}
              data-skel-cell=""
              className={`p-2 ${
                c === 0 && firstSticky ? "min-w-[150px]" : "flex-1 border-l border-border-default"
              }`}
            >
              <SkeletonBlock w={c === 0 ? "80%" : "50%"} h="0.875rem" />
            </div>
          ))}
        </div>
      ))}
    </Region>
  );
}

/**
 * ProfileView dark hero + avatar + tab row. The hero is an always-Night band,
 * so `surface-night` re-tokens the subtree and the shimmer veils resolve to
 * their paper-alpha Night values on their own — no per-block overrides.
 */
export function SkeletonHero() {
  return (
    <Region>
      <div className="surface-night rounded-lg bg-canvas p-5 sm:p-6">
        <div className="flex items-center gap-4">
          <SkeletonBlock w="64px" h="64px" rounded="rounded-full" />
          <div className="flex-1 space-y-2">
            <SkeletonBlock w="40%" h="1.5rem" />
            <SkeletonBlock w="60%" h="0.875rem" />
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonBlock key={i} w="64px" h="1rem" />
        ))}
      </div>
    </Region>
  );
}

/** Fields are 44px tall and 12px-rounded in v18 — the placeholder matches. */
export function SkeletonFormSection({ fields = 4 }: { fields?: number }) {
  return (
    <Region className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-1.5">
          <SkeletonBlock w="30%" h="0.75rem" />
          <SkeletonBlock w="100%" h="2.75rem" rounded="rounded-md" />
        </div>
      ))}
    </Region>
  );
}
